import {
  ComputeBudgetInstruction,
  ComputeBudgetProgram,
  Connection,
  Transaction,
  TransactionInstruction,
  VersionedTransaction,
} from '@solana/web3.js';

export function extractComputeUnitLimit(tx: VersionedTransaction | Transaction) {
  if ('version' in tx) {
    for (let ix of tx.message.compiledInstructions) {
      let programId = tx.message.staticAccountKeys[ix.programIdIndex];
      if (programId && ComputeBudgetProgram.programId.equals(programId)) {
        if (ix.data[0] === 2) {
          const decodedTI = new TransactionInstruction({
            keys: [],
            programId: ComputeBudgetProgram.programId,
            data: Buffer.from(ix.data),
          });
          return ComputeBudgetInstruction.decodeSetComputeUnitLimit(decodedTI).units;
        }
      }
    }
  } else {
    for (let ix of tx.instructions) {
      if (ComputeBudgetProgram.programId.equals(ix.programId)) {
        if (ix.data[0] === 2) {
          return ComputeBudgetInstruction.decodeSetComputeUnitLimit(ix).units;
        }
      }
    }
  }
  return null;
}

export function extractComputeUnitPrice(tx: VersionedTransaction | Transaction) {
  if ('version' in tx) {
    for (let ix of tx.message.compiledInstructions) {
      let programId = tx.message.staticAccountKeys[ix.programIdIndex];
      if (programId && ComputeBudgetProgram.programId.equals(programId)) {
        if (ix.data[0] === 3) {
          const decodedTI = new TransactionInstruction({
            keys: [],
            programId: ComputeBudgetProgram.programId,
            data: Buffer.from(ix.data),
          });
          return ComputeBudgetInstruction.decodeSetComputeUnitPrice(decodedTI).microLamports;
        }
      }
    }
  } else {
    for (let ix of tx.instructions) {
      if (ComputeBudgetProgram.programId.equals(ix.programId)) {
        if (ix.data[0] === 3) {
          return ComputeBudgetInstruction.decodeSetComputeUnitPrice(ix).microLamports;
        }
      }
    }
  }
  return null;
}

export const modifyComputeUnitLimitIx = (
  tx: VersionedTransaction | Transaction,
  newComputeUnitLimit: number,
): boolean => {
  if ('version' in tx) {
    for (let ix of tx.message.compiledInstructions) {
      let programId = tx.message.staticAccountKeys[ix.programIdIndex];
      if (programId && ComputeBudgetProgram.programId.equals(programId)) {
        // need check for data index
        if (ix.data[0] === 2) {
          ix.data = Uint8Array.from(ComputeBudgetProgram.setComputeUnitLimit({ units: newComputeUnitLimit }).data);
          return true;
        }
      }
    }
    // could not inject for VT
  } else {
    for (let ix of tx.instructions) {
      if (ComputeBudgetProgram.programId.equals(ix.programId)) {
        // need check for data index
        if (ix.data[0] === 2) {
          ix.data = ComputeBudgetProgram.setComputeUnitLimit({ units: newComputeUnitLimit }).data;
          return true;
        }
      }
    }

    // inject if none
    tx.add(ComputeBudgetProgram.setComputeUnitLimit({ units: newComputeUnitLimit }));
    return true;
  }

  return false;
};

/**
 * Modify priority fee in transaction
 * @param tx
 * @param newPriorityFee
 * @returns {boolean} true if priority fee was modified
 **/
export const modifyComputeUnitPriceIx = (tx: VersionedTransaction | Transaction, newPriorityFee: number): boolean => {
  if ('version' in tx) {
    for (let ix of tx.message.compiledInstructions) {
      let programId = tx.message.staticAccountKeys[ix.programIdIndex];
      if (programId && ComputeBudgetProgram.programId.equals(programId)) {
        // need check for data index
        if (ix.data[0] === 3) {
          ix.data = Uint8Array.from(ComputeBudgetProgram.setComputeUnitPrice({ microLamports: newPriorityFee }).data);
          return true;
        }
      }
    }
    // could not inject for VT
  } else {
    for (let ix of tx.instructions) {
      if (ComputeBudgetProgram.programId.equals(ix.programId)) {
        // need check for data index
        if (ix.data[0] === 3) {
          ix.data = ComputeBudgetProgram.setComputeUnitPrice({ microLamports: newPriorityFee }).data;
          return true;
        }
      }
    }

    // inject if none
    tx.add(ComputeBudgetProgram.setComputeUnitPrice({ microLamports: newPriorityFee }));
    return true;
  }

  return false;
};

/**
 * Estimate and modify priority fee in transaction
 * @param connection
 * @param tx
 * @param newPriorityFee
 * @returns {number} estimated priority fee
 *  **/
export const estimatePriorityFee = async (
  connection: Connection,
  params: { tx?: VersionedTransaction | Transaction; accounts?: [] },
): Promise<number> => {
  let writeableAccounts = [];

  if (params.tx) {
    if ('version' in params.tx) {
      const accounts = params.tx.message.staticAccountKeys;
      for (const [idx, account] of accounts.entries()) {
        if (params.tx.message.isAccountWritable(idx)) {
          writeableAccounts.push(account);
        }
      }
    } else {
      for (const instruction of params.tx.instructions) {
        for (const account of instruction.keys) {
          if (account.isWritable) {
            writeableAccounts.push(account.pubkey);
          }
        }
      }
    }
  }

  if (params.accounts) {
    writeableAccounts = writeableAccounts.concat(params.accounts);
  }

  if (writeableAccounts.length) {
    let fees = await connection.getRecentPrioritizationFees({
      lockedWritableAccounts: writeableAccounts,
    });
    fees = fees.sort((feeA, feeB) => feeB.slot - feeA.slot);
    fees = fees.slice(0, 20);
    let last20SortedFees = fees.map((fee) => fee.prioritizationFee).sort((feeA, feeB) => feeB - feeA);
    let priorityFee = Math.max(last20SortedFees[Math.floor((last20SortedFees.length * 85) / 100)], 1); // minimum 1 microLamport

    return priorityFee;
  }

  return 1; // default 1
};
